"use client";

import { useMemo } from "react";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

import type { PrashnaLabels } from "./PrashnaCopy";

/**
 * Split out of `PrashnaClient.tsx` on 2026-09-08 when the rule-58 result reserve
 * pushed that file past the 500-line ceiling (G03). Relocated verbatim.
 *
 * The continuation copy deliberately does NOT repeat the page's "no birth
 * details" promise — the chat still asks for them. It promises only what it
 * delivers: the same question, carried forward in the seeker's own words.
 */
const PRASHNA_SOURCE_PATH = { en: "/prashna-kundali", hi: "/hi/prashna-kundali" } as const;
const FOLLOW_UP_PLACEMENT = "prashna_result_followup";

/**
 * Horary's forward step is another question, never a birth-details form
 * (rules 40/43). The page-bottom SEOAgentBridge cannot carry the seeker's words —
 * it stamps the canned intent prompt on click — so the continuation that DOES
 * carry them lives here, inside the result region: it renders only once an answer
 * exists, so the pre-answer surface keeps exactly one CTA (rule 39).
 * Telemetry is click-only on purpose: this card sits below the full result stack,
 * so a mount-fired impression would manufacture reach it never had (rule 47). No
 * `purchase_intent_*` either — that score ranks BUYING propensity and this
 * continuation leads to the free chat, not an offer.
 */
export function PrashnaFollowUpCTA({
  locale,
  t,
  getQuestion,
}: {
  locale: "en" | "hi";
  t: PrashnaLabels;
  getQuestion: () => string;
}) {
  // Resolve the destination through the intent registry so it cannot drift.
  const { config, sourcePath, href, payload } = useMemo(() => {
    const source = PRASHNA_SOURCE_PATH[locale];
    const resolved = getSEOIntentConfigForSource(source);
    return {
      config: resolved,
      sourcePath: source,
      href: buildSEOAgentHref(resolved, source, locale),
      payload: {
        source_path: source,
        intent: resolved.intent,
        page_category: resolved.pageCategory,
        offer_type: resolved.primaryOffer,
        plan_id: resolved.reportPlanId,
        placement: FOLLOW_UP_PLACEMENT,
      },
    };
  }, [locale]);

  function handleClick() {
    // The chat auto-sends seo_entry_prompt as its first message, so writing the
    // typed question here is what makes the next screen open on the question the
    // prashna chart just answered instead of a generic greeting.
    const question = getQuestion().trim() || config.prompt;
    try {
      safeSessionSet("seo_entry_intent", config.intent);
      safeSessionSet("seo_entry_path", sourcePath);
      safeSessionSet("seo_entry_page_category", config.pageCategory);
      safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
      safeSessionSet("seo_entry_prompt", question);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage unavailable — the link still opens the chat, just without auto-send.
    }
    void import("@/lib/analytics-seo-bridge")
      .then(({ trackSEOAgentBridgeClicked, trackSEOIntentCaptured }) => {
        trackSEOAgentBridgeClicked({ ...payload, target: config.target });
        trackSEOIntentCaptured({
          source_path: sourcePath,
          intent: config.intent,
          page_category: config.pageCategory,
          target: config.target,
          placement: FOLLOW_UP_PLACEMENT,
        });
      })
      .catch(() => {});
  }

  return (
    <a
      href={href}
      onClick={handleClick}
      data-prashna-followup="inline"
      className="mt-5 flex flex-col gap-3 rounded-xl border border-border-default bg-surface-brand-subtle p-5 no-underline transition-shadow hover:shadow-md sm:flex-row sm:items-center sm:justify-between"
    >
      <span className="min-w-0">
        <span className="block text-body-small font-semibold text-text-primary">
          {t.followUpHeadline}
        </span>
        <span className="mt-1 block text-caption text-text-secondary">{t.followUpSubhead}</span>
      </span>
      <span className="shrink-0 self-start rounded-lg bg-surface-brand-strong px-4 py-2.5 text-button font-semibold text-text-on-brand sm:self-center">
        {t.followUpCta}
      </span>
    </a>
  );
}
